import { Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { environment } from '../../../../../environments';
import { ITelemetryProvider } from './telemetry-provider.interface';

/**
 * Application Insights implementation of the telemetry provider.
 */
@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsTelemetryProvider implements ITelemetryProvider {
  private appInsights: ApplicationInsights | null = null;
  private customProperties: Record<string, string> = {};

  constructor() {
    this.initializeAppInsights();
  }

  private initializeAppInsights(): void {
    // Skip initialization if telemetry is disabled
    if (environment.applicationInsights?.enabled === false) {
      return;
    }

    // Build connection string with custom backend as ingestion endpoint
    // Note: Instrumentation key is a placeholder - actual routing happens via custom backend
    const instrumentationKey = '00000000-0000-0000-0000-000000000000';
    // LiveEndpoint is not used since all telemetry goes through our backend - this is just a placeholder
    const unusedLiveEndpoint = 'https://not-used.example.com/';
    const connectionString = `InstrumentationKey=${instrumentationKey};IngestionEndpoint=${environment.apiUrl}/;LiveEndpoint=${unusedLiveEndpoint}`;

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true, // Automatically track Angular route changes
        enableCorsCorrelation: true, // Enable correlation for CORS requests
        correlationHeaderExcludedDomains: ['*.prodpad.com'],
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        disableFetchTracking: false,
        disableExceptionTracking: false, // Track unhandled exceptions
        autoExceptionInstrumented: true, // Automatically instrument exception tracking
        enableUnhandledPromiseRejectionTracking: true, // Track unhandled promise rejections
      },
    });

    this.appInsights.loadAppInsights();

    // Set cloud role name to identify this application in shared Application Insights
    const cloudRoleName = environment.applicationInsights?.cloudRoleName;
    if (cloudRoleName) {
      this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
        if (envelope.tags) {
          envelope.tags['ai.cloud.role'] = cloudRoleName;
        }
      });
    }

    // Add custom properties to all telemetry
    this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      // Add custom properties to baseData.properties (for page views, events, etc.)
      if (envelope.baseData) {
        if (!envelope.baseData['properties']) {
          envelope.baseData['properties'] = {};
        }
        Object.assign(envelope.baseData['properties'], this.customProperties);
      }

      // Add custom properties to data.baseData.properties (for dependencies, requests)
      if (envelope.data?.['baseData']) {
        if (!envelope.data['baseData']['properties']) {
          envelope.data['baseData']['properties'] = {};
        }
        Object.assign(envelope.data['baseData']['properties'], this.customProperties);
      }
    });

    this.appInsights.trackPageView(); // Initial page view
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name }, properties);
    }
  }

  trackPageView(name?: string, uri?: string): void {
    if (this.appInsights) {
      this.appInsights.trackPageView({ name, uri });
    }
  }

  trackException(error: Error, severityLevel?: number): void {
    if (this.appInsights) {
      this.appInsights.trackException({
        exception: error,
        severityLevel,
      });
    }
  }

  trackMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    if (this.appInsights) {
      this.appInsights.trackMetric({ name, average }, properties);
    }
  }

  trackTrace(message: string, properties?: Record<string, unknown>): void {
    if (this.appInsights) {
      this.appInsights.trackTrace({ message }, properties);
    }
  }

  setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string): void {
    if (this.appInsights) {
      this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId, true);
      // Add user identity to custom properties for all subsequent telemetry
      // Using 'principal.identity' to align with backend telemetry naming
      this.setCustomProperty('principal.identity', authenticatedUserId);
    }
  }

  clearAuthenticatedUserContext(): void {
    if (this.appInsights) {
      this.appInsights.clearAuthenticatedUserContext();
      // Remove user identity from custom properties
      this.removeCustomProperty('principal.identity');
    }
  }

  setCustomProperty(key: string, value: string): void {
    this.customProperties[key] = value;
  }

  removeCustomProperty(key: string): void {
    delete this.customProperties[key];
  }

  clearCustomProperties(): void {
    this.customProperties = {};
  }

  flush(): void {
    if (this.appInsights) {
      this.appInsights.flush();
    }
  }
}
